import { env } from '@/shared/config/env';

import { ApiError, NETWORK_STATUS, type ApiErrorShape } from './errors';

/**
 * The single way the application talks to Laravel.
 *
 * Authentication is Sanctum's SPA cookie flow, so every request carries
 * credentials and no token is ever held in JavaScript. Writes need the CSRF
 * cookie first; it is fetched once and reused, because the browser keeps
 * sending it afterwards.
 */

/**
 * Envelope every endpoint replies with.
 *
 * `meta` and `links` come from Laravel's paginator and are snake_case, unlike
 * the payload itself — that is the framework's shape, not an inconsistency to
 * paper over here.
 */
export type ApiEnvelope<T> = {
  data: T;
  meta?: Record<string, unknown>;
  links?: Record<string, unknown>;
};

const API_ROOT = `${env.apiUrl.replace(/\/$/, '')}/v1`;

/** Sanctum serves this from the origin root, not from under /api. */
const CSRF_PATH = `${env.apiUrl.replace(/\/api\/?$/, '')}/sanctum/csrf-cookie`;

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

const readCookie = (name: string): string | undefined =>
  document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`))
    ?.split('=')[1];

let csrfRequest: Promise<void> | null = null;

/** Fetches the CSRF cookie once per session; concurrent writes share the call. */
const ensureCsrfCookie = async (): Promise<void> => {
  if (readCookie('XSRF-TOKEN')) return;
  csrfRequest ??= fetch(CSRF_PATH, { credentials: 'include' }).then(() => undefined);

  try {
    await csrfRequest;
  } catch {
    // This fetch sits before the guarded one, so without this a dropped
    // connection escaped as a raw rejection and was reported as "something
    // went wrong" instead of "cannot reach the server". Clearing the promise
    // lets the next write try again.
    csrfRequest = null;

    throw new ApiError({
      code: 'network',
      message: 'The request did not reach the server.',
      status: NETWORK_STATUS,
    });
  }
};

/** Only for tests: forget that the cookie was already fetched. */
export const resetCsrfState = () => {
  csrfRequest = null;
};

const toApiError = async (response: Response): Promise<ApiError> => {
  const fallback: ApiErrorShape = {
    code: 'server_error',
    message: response.statusText || 'Request failed.',
    status: response.status,
  };

  try {
    const body: unknown = await response.json();
    const error = (body as { error?: Partial<ApiErrorShape> })?.error;
    if (!error) return new ApiError(fallback);

    return new ApiError({
      ...fallback,
      ...error,
      // Trust the transport over the body: the status line cannot be spoofed
      // by a proxy rewriting JSON.
      status: response.status,
    });
  } catch {
    return new ApiError(fallback);
  }
};

const request = async <T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> => {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  // Tells Laravel this is an AJAX call, so it answers 401 instead of redirecting.
  headers.set('X-Requested-With', 'XMLHttpRequest');
  // FormData sets its own Content-Type, including the multipart boundary the
  // server needs to parse it. Setting one here would destroy that boundary.
  if (init.body !== undefined && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (!SAFE_METHODS.has(method)) {
    await ensureCsrfCookie();
    const token = readCookie('XSRF-TOKEN');
    if (token) headers.set('X-XSRF-TOKEN', decodeURIComponent(token));
  }

  let response: Response;
  try {
    response = await fetch(`${API_ROOT}${path}`, {
      ...init,
      method,
      headers,
      credentials: 'include',
    });
  } catch {
    throw new ApiError({
      code: 'network',
      message: 'The request did not reach the server.',
      status: NETWORK_STATUS,
    });
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return { data: undefined as T };

  return (await response.json()) as ApiEnvelope<T>;
};

const withBody = (body: unknown): RequestInit => {
  if (body === undefined) return {};

  // An upload passes through untouched. Stringifying it would send the literal
  // "{}" and lose the file — silently, because the request still succeeds.
  return body instanceof FormData ? { body } : { body: JSON.stringify(body) };
};

/** Verbs return the payload; use `page` when the meta block matters. */
export const api = {
  get: async <T>(path: string, init?: RequestInit): Promise<T> =>
    (await request<T>(path, { ...init, method: 'GET' })).data,

  page: <T>(path: string, init?: RequestInit): Promise<ApiEnvelope<T>> =>
    request<T>(path, { ...init, method: 'GET' }),

  post: async <T>(path: string, body?: unknown): Promise<T> =>
    (await request<T>(path, { ...withBody(body), method: 'POST' })).data,

  patch: async <T>(path: string, body?: unknown): Promise<T> =>
    (await request<T>(path, { ...withBody(body), method: 'PATCH' })).data,

  put: async <T>(path: string, body?: unknown): Promise<T> =>
    (await request<T>(path, { ...withBody(body), method: 'PUT' })).data,

  delete: async <T>(path: string, body?: unknown): Promise<T> =>
    (await request<T>(path, { ...withBody(body), method: 'DELETE' })).data,
};
